import { useEffect, useState } from 'react';
import { api, type PublicSnapshot } from '../lib/api';
import CiscoStatusCallout from '../components/CiscoStatusCallout';

function tierClass(t: PublicSnapshot['overall']) {
  if (t === 'operational') return 'text-tnh-ok';
  if (t === 'degraded') return 'text-tnh-warn';
  return 'text-tnh-err';
}

export default function Status() {
  const [snap, setSnap] = useState<PublicSnapshot | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const load = () => api.status().then(setSnap).catch((e) => setErr(e.message));
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Status</h1>
      <CiscoStatusCallout scope="both" />
        {err && <div className="card p-5 text-tnh-err">Status feed unreachable: {err}</div>}
        {!snap && !err && <div className="text-gray-500">Loading…</div>}
        {snap && (
          <>
            <h1 className={`text-3xl font-bold ${tierClass(snap.overall)}`}>
              {snap.overall === 'operational' ? 'All systems operational' : snap.overall.replace('_', ' ')}
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Refreshed {new Date(snap.generated_at).toLocaleString()}
            </p>

            <h2 className="mt-10 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Services
            </h2>
            <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
              {Object.entries(snap.services).map(([key, status]) => (
                <div key={key} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <div className="font-medium">{key}</div>
                  </div>
                  <div className={`text-sm font-medium ${tierClass(status)}`}>
                    {status === 'operational' ? 'Operational' : status.replace('_', ' ')}
                  </div>
                </div>
              ))}
            </div>

            <h2 className="mt-10 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Spokes
            </h2>
            <div className="card p-5 flex gap-8 text-sm">
              <div><span className="font-semibold">{snap.spokes.total}</span> total</div>
              <div className="text-tnh-ok"><span className="font-semibold">{snap.spokes.healthy}</span> healthy</div>
              <div className="text-tnh-warn"><span className="font-semibold">{snap.spokes.degraded}</span> degraded</div>
              <div className="text-tnh-err"><span className="font-semibold">{snap.spokes.stale}</span> stale</div>
            </div>

            {snap.external_dependencies && snap.external_dependencies.length > 0 && (
              <>
                <h2 className="mt-10 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                  External dependencies
                </h2>
                <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
                  {snap.external_dependencies.map((dep) => (
                    <div key={dep.key} className="flex items-center justify-between px-5 py-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{dep.display_name}</div>
                        {dep.note && (
                          <div className="text-xs text-gray-500 mt-0.5">{dep.note}</div>
                        )}
                      </div>
                      <a
                        href={dep.status_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-tnh-accent hover:underline whitespace-nowrap ml-4"
                      >
                        {dep.tier
                          ? (dep.tier === 'operational' ? 'Operational' : dep.tier.replace('_', ' '))
                          : 'Check upstream status →'}
                      </a>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
    </div>
  );
}
